import { create } from "zustand";
import * as yaml from "js-yaml";
import { WeBuildAiAdmin } from "@webuild-ai/admin";
import { WebuildAiPlatform } from "@webuild-ai/agent-platform";
import i18next from "i18next";

import { ChatPersonalityType } from "@/components/Chat/ChatPersonality";
import { applyAgentToolPreference, useToolPreferencesStore } from "@/stores/ToolPreferencesStore";
import { isValidWorkflowId, generateWorkflowId } from "@/utils/workflowId";
import { SYSTEM_WORKFLOW_IDS } from "@/utils/workflowUtils";
import { logger } from "@/telemetry";
import { preserveWorkflowStatus, stripReservedInputKeys } from "@/utils/workflowStatus";

export interface UserInteractionConfig {
  maxTurns?: number | null | undefined;
  terminationCondition?: Array<string> | null | undefined;
}

export interface Agent {
  type: string;
  parameters: Record<string, any>;
  triggers: Trigger[];
  userInteraction?: UserInteractionConfig | null | undefined;
  team?: string | null | undefined;
  description?: string | null | undefined;
}

export type TeamStrategy = "selector" | "mixture";

// YAML snake_case versions of interfaces (for parsing YAML files)
interface YamlUserInteractionConfig {
  max_turns?: number | null;
  termination_condition?: Array<string> | null;
}

interface YamlAgent {
  type: string;
  parameters: Record<string, any>;
  triggers: Trigger[];
  /**
   * Inbound only, and optional. The engine's `AgentConfig`
   * (`agent-platform/src/pathway_engine/workflow_schema.py`) declares no
   * `tools` field at all, so an agent's connectors live in
   * `parameters.tools` and nowhere else. This key survives because a
   * definition may still arrive with it — the `workflow_generator` build agent
   * emits it, and older stored YAML carries it — so it is read and folded into
   * `parameters`. It is never written back out; see `transformAgentToYaml`.
   */
  tools?: string[] | null;
  user_interaction?: YamlUserInteractionConfig | null;
  team?: string | null;
  description?: string | null;
}

export interface Team {
  members: string[];
  strategy?: TeamStrategy | undefined;
  maxCycles?: number | undefined;
  goal: string;
  useLlmJudgment?: boolean | undefined;
  selectorPrompt?: string | null | undefined;
  aggregatorPrompt?: string | null | undefined;
  triggers: Trigger[];
  model?: string | undefined;
}

export interface Trigger {
  source: string;
  mapping: Record<string, any>;
}

export interface Workflow {
  name: string;
  id: string;
  tenant: string;
  description: string;
  input: Record<string, any> | null | undefined;
  teams?: Record<string, Team> | null | undefined;
  agents: Record<string, Agent>;
}

export interface LoadWorkflowOptions {
  /**
   * Whether the signed-in user's stored tool selections may be layered over
   * the definition once it loads. True (the default) for chat, where the tool
   * picker is a personal override of a workflow shared with the whole tenant.
   *
   * False for the editors, where the tools on screen *are* the definition
   * under edit. The overlay is undone again on save (`stripToolPreferences`)
   * and on serialisation (`workflowToYamlObject`), and neither of those can
   * tell a preference apart from a deliberate change, so with the overlay
   * armed every tool edit made in the Workflow Builder or the Playground was
   * silently reverted: the properties panel's select and a hand-written
   * `tools:` entry in the Code view both wrote to the store and were both
   * overwritten from the load-time snapshot before they could be saved.
   */
  applyToolPreferences?: boolean;
}

export interface WorkflowStore {
  workflow: Workflow | null;
  isLoadingWorkflow: boolean;
  setWorkflow: (workflow: Workflow | null) => void;
  setWorkflowFromYaml: (yamlString: string, platformClient: WebuildAiPlatform) => Promise<void>;
  setWorkflowName: (name: string) => void;
  setWorkflowDescription: (description: string) => void;
  getWorkflow: () => Workflow | null;
  loadWorkflow: (
    workflowId: string,
    adminClient: WeBuildAiAdmin,
    options?: LoadWorkflowOptions,
  ) => Promise<void>;
  saveWorkflow: (adminClient: WeBuildAiAdmin) => Promise<Workflow | null>;
  setPersonality: (personality: ChatPersonalityType) => void;
  updateAgentTools: (agent_id: string, tool_id: string, isSelected: boolean) => void;
}

// Transform snake_case team fields from YAML to camelCase for SDK compatibility
// YAML uses snake_case (e.g., max_cycles), but SDK expects camelCase (e.g., maxCycles)
interface YamlTeam {
  members: string[];
  strategy?: TeamStrategy;
  max_cycles?: number;
  goal: string;
  use_llm_judgment?: boolean;
  selector_prompt?: string | null;
  aggregator_prompt?: string | null;
  triggers: Trigger[];
  model?: string;
}

function transformTeamFromYaml(yamlTeam: YamlTeam): Team {
  return {
    members: yamlTeam.members,
    strategy: yamlTeam.strategy,
    maxCycles: yamlTeam.max_cycles,
    goal: yamlTeam.goal,
    useLlmJudgment: yamlTeam.use_llm_judgment,
    selectorPrompt: yamlTeam.selector_prompt,
    aggregatorPrompt: yamlTeam.aggregator_prompt,
    triggers: yamlTeam.triggers ?? [],
    model: yamlTeam.model,
  };
}

function transformTeamsFromYaml(
  yamlTeams: Record<string, YamlTeam> | null | undefined,
): Record<string, Team> | null | undefined {
  if (!yamlTeams) return yamlTeams;

  const transformedTeams: Record<string, Team> = {};

  for (const [teamId, team] of Object.entries(yamlTeams)) {
    transformedTeams[teamId] = transformTeamFromYaml(team);
  }

  return transformedTeams;
}

// Transform agent's user_interaction from snake_case to camelCase
function transformUserInteractionFromYaml(
  yamlConfig: YamlUserInteractionConfig | null | undefined,
): UserInteractionConfig | null | undefined {
  if (!yamlConfig) return yamlConfig;

  return {
    maxTurns: yamlConfig.max_turns,
    terminationCondition: yamlConfig.termination_condition,
  };
}

function transformAgentFromYaml(yamlAgent: YamlAgent): Agent {
  // Move tools into parameters to match standardized structure
  const parameters = { ...yamlAgent.parameters };

  // A legacy agent-level list is folded in only when it actually says
  // something. The old guard was a bare truthiness check, and an empty array is
  // truthy, so a document carrying both `parameters.tools` and a stray
  // `tools: []` silently disconnected every connector on that agent.
  if (Array.isArray(yamlAgent.tools)) {
    if (yamlAgent.tools.length > 0 || parameters.tools === undefined) {
      parameters.tools = yamlAgent.tools;
    }
  }

  return {
    type: yamlAgent.type,
    parameters,
    triggers: yamlAgent.triggers ?? [],
    userInteraction: transformUserInteractionFromYaml(yamlAgent.user_interaction),
    team: yamlAgent.team,
    description: yamlAgent.description,
  };
}

function transformAgentsFromYaml(yamlAgents: Record<string, YamlAgent>): Record<string, Agent> {
  const transformedAgents: Record<string, Agent> = {};

  for (const [agentId, agent] of Object.entries(yamlAgents)) {
    transformedAgents[agentId] = transformAgentFromYaml(agent);
  }

  return transformedAgents;
}

// Reverse transforms: camelCase model → snake_case for YAML serialization
// Without these, yaml.stringify(workflow) outputs camelCase keys which break
// the snake_case-only transform functions on subsequent edits (lossy round-trip).

function transformUserInteractionToYaml(
  config: UserInteractionConfig | null | undefined,
): YamlUserInteractionConfig | null | undefined {
  if (!config) return config;

  return {
    max_turns: config.maxTurns,
    termination_condition: config.terminationCondition,
  };
}

function transformAgentToYaml(agent: Agent): YamlAgent {
  return {
    type: agent.type,
    parameters: agent.parameters,
    triggers: agent.triggers ?? [],
    // `tools` is deliberately absent. It used to be emitted alongside
    // `parameters`, which already holds the same list, so the Code view showed
    // every agent's connectors twice and anything copied out of the editor
    // carried a key the engine's `AgentConfig` does not define.
    user_interaction: transformUserInteractionToYaml(agent.userInteraction),
    team: agent.team,
    description: agent.description,
  };
}

function transformTeamToYaml(team: Team): YamlTeam {
  return {
    members: team.members,
    strategy: team.strategy,
    max_cycles: team.maxCycles,
    goal: team.goal,
    use_llm_judgment: team.useLlmJudgment,
    selector_prompt: team.selectorPrompt,
    aggregator_prompt: team.aggregatorPrompt,
    triggers: team.triggers ?? [],
    model: team.model,
  };
}

/**
 * Converts a camelCase Workflow model to a snake_case object suitable for YAML serialization.
 * This ensures the YAML editor always displays snake_case keys matching the backend format,
 * preventing the lossy round-trip where camelCase YAML gets re-parsed by snake_case-only transforms.
 */
export function workflowToYamlObject(
  workflow: Workflow,
  baseAgentTools?: Record<string, string[]> | null,
): Record<string, unknown> {
  // Strip per-user tool preferences so the YAML editor shows the shared workflow definition
  const baseTools = baseAgentTools ?? _baseAgentTools;
  const cleanWorkflow = baseTools ? stripToolPreferences(workflow, baseTools) : workflow;

  const agents: Record<string, YamlAgent> = {};

  for (const [agentId, agent] of Object.entries(cleanWorkflow.agents)) {
    agents[agentId] = transformAgentToYaml(agent);
  }

  let teams: Record<string, YamlTeam> | null | undefined = workflow.teams ? {} : workflow.teams;

  if (workflow.teams && teams) {
    for (const [teamId, team] of Object.entries(workflow.teams)) {
      teams[teamId] = transformTeamToYaml(team);
    }
  }

  // Some fields (icon, chatManagerPrompt, scheduled) exist at runtime from YAML spread
  // but aren't in the Workflow interface — access via indexing to preserve them.
  const extra = workflow as any;

  return {
    name: workflow.name,
    id: workflow.id,
    description: workflow.description,
    tenant: workflow.tenant,
    icon: extra["icon"] ?? null,
    chat_manager_prompt: extra["chatManagerPrompt"] ?? extra["chat_manager_prompt"] ?? null,
    scheduled: extra["scheduled"] ?? null,
    // Reserved bookkeeping keys are stripped, never serialised. `pathway_status`
    // and its "Managed by Pathway. Do not edit." description are an internal
    // convention of this frontend (ADR-0021/0022) that neither admin nor
    // agent-platform knows about, and the only caller of this function is the
    // Workflow Builder's Code view, so serialising the stored shape put the
    // convention in front of the user and into anything they copied out of the
    // editor. `setWorkflowFromYaml` carries the status back across the
    // round-trip, exactly as it does for `id` and `scheduled`.
    input: stripReservedInputKeys(workflow.input),
    teams,
    agents,
  };
}

/**
 * Original (pre-preference) tool lists per agent, keyed by agentId.
 * Module-private — not part of the public store interface.
 */
let _baseAgentTools: Record<string, string[]> | null = null;

/** The workflow `_baseAgentTools` was captured from, so callers can prove identity. */
let _baseWorkflowId: string | null = null;

/**
 * Read-only accessor for `_baseAgentTools`.
 *
 * The snapshot is a module global with no workflow identity of its own, so a
 * caller recording a preference must say which workflow it believes it is
 * recording against. A workflow switch between render and click would otherwise
 * key a delta to one workflow while using another's definition as the baseline,
 * inverting `enabled` and `disabled` for every tool the two do not share.
 * Passing no id keeps the old unchecked read, which is what the Code view and
 * the save path want: they only ask whether an overlay is in play at all.
 */
export function getBaseAgentTools(workflowId?: string): Record<string, string[]> | null {
  if (workflowId !== undefined && _baseWorkflowId !== workflowId) return null;

  return _baseAgentTools;
}

/** Set or clear the snapshot and the workflow identity it belongs to, together. */
function setBaseAgentTools(
  tools: Record<string, string[]> | null,
  workflowId: string | null,
): void {
  _baseAgentTools = tools;
  _baseWorkflowId = tools ? workflowId : null;
}

/** Extract the original tool lists from a workflow before preferences are applied. */
function captureBaseAgentTools(workflow: Workflow): Record<string, string[]> {
  const base: Record<string, string[]> = {};

  for (const [agentId, agent] of Object.entries(workflow.agents)) {
    base[agentId] = [...(agent.parameters?.tools || [])];
  }

  return base;
}

/**
 * Apply per-user tool preferences over a workflow's default tool configuration.
 *
 * The stored preference is a difference from the shared definition, not a copy
 * of it, so this removes what the user switched off and appends what they
 * switched on. A connector added to the definition since the preference was
 * recorded is in neither set and therefore survives (LSEG-190). It used to be
 * substituted wholesale, which silently dropped every such connector on the way
 * to `platformClient.workflows.create`.
 */
function applyToolPreferences(workflow: Workflow): Workflow {
  const prefs = useToolPreferencesStore.getState();

  if (!prefs.hasPreferences(workflow.id)) return workflow;

  const mergedAgents = { ...workflow.agents };

  for (const [agentId, agent] of Object.entries(mergedAgents)) {
    const preference = prefs.getAgentPreference(workflow.id, agentId);

    if (preference === undefined) continue;

    mergedAgents[agentId] = {
      ...agent,
      parameters: {
        ...agent.parameters,
        tools: applyAgentToolPreference(agent.parameters?.tools || [], preference),
      },
    };
  }

  return { ...workflow, agents: mergedAgents };
}

/**
 * Restore the original (shared) tool configuration, stripping any per-user overrides.
 * Used before saving or serialising to YAML to prevent personal preferences from
 * polluting the shared workflow definition.
 */
function stripToolPreferences(workflow: Workflow, baseTools: Record<string, string[]>): Workflow {
  const restoredAgents = { ...workflow.agents };

  for (const [agentId, agent] of Object.entries(restoredAgents)) {
    if (agentId in baseTools) {
      restoredAgents[agentId] = {
        ...agent,
        parameters: { ...agent.parameters, tools: baseTools[agentId] },
      };
    }
  }

  return { ...workflow, agents: restoredAgents };
}

//TODO: load a baseline workflow
export const useWorkflowStore = create<WorkflowStore>((set, get) => ({
  workflow: null,
  isLoadingWorkflow: false,

  updateAgentTools: (agent_id: string, tool_id: string, isSelected: boolean) => {
    const workflow = get().workflow;

    if (!workflow) return;
    const currentTools = workflow.agents[agent_id].parameters?.tools || [];
    const updatedTools = isSelected
      ? [...currentTools, tool_id]
      : currentTools.filter((tool: string) => tool !== tool_id);

    // Update in both formats to ensure consistency with backend
    const updatedWorkflow = {
      ...workflow,
      agents: {
        ...workflow.agents,
        [agent_id]: {
          ...workflow.agents[agent_id],
          parameters: {
            ...workflow.agents[agent_id].parameters,
            tools: updatedTools,
          },
        },
      },
    };

    set({ workflow: updatedWorkflow });
  },

  setWorkflow: (workflow: Workflow | null) => {
    const currentWorkflow = get().workflow;

    // Only update if the workflow is different
    if (JSON.stringify(workflow) !== JSON.stringify(currentWorkflow)) {
      // The base snapshot belongs to one loaded workflow, so a different
      // workflow — or none at all — invalidates it. Without this, opening the
      // Builder's "New workflow" after a chat session left the snapshot of
      // whatever chat last loaded in place, and any agent id the build agent
      // happened to reuse had that workflow's tools restored over it on save.
      if (!workflow || workflow.id !== currentWorkflow?.id) {
        setBaseAgentTools(null, null);
      }

      set({ workflow });
    }
  },

  loadWorkflow: async (
    workflowId: string,
    adminClient: WeBuildAiAdmin,
    options?: LoadWorkflowOptions,
  ) => {
    logger.info("🔄 loadWorkflow started", { workflowId, hasAdminClient: String(!!adminClient) });
    const withToolPreferences = options?.applyToolPreferences ?? true;

    /**
     * Removes a preference overlay the store is already carrying, for the case
     * where an editor asks for a workflow chat has just loaded. Both early
     * returns below skip the fetch, so without this the editor opens on the
     * personal overlay and saves it back to the shared definition.
     */
    const dropOverlayForEditing = () => {
      if (withToolPreferences) return;

      const loaded = get().workflow;
      const baseTools = _baseAgentTools;

      if (loaded && baseTools) {
        set({ workflow: stripToolPreferences(loaded, baseTools) });
      }

      setBaseAgentTools(null, null);
    };
    const currentWorkflow = get().workflow;
    const isCurrentlyLoading = get().isLoadingWorkflow;

    logger.info("📋 currentWorkflow state", {
      hasCurrentWorkflow: String(!!currentWorkflow),
      currentWorkflowId: String(currentWorkflow?.id),
      currentWorkflowName: String(currentWorkflow?.name),
      requestedWorkflowId: workflowId,
      isCurrentlyLoading: String(isCurrentlyLoading),
    });

    // Check if the workflow is already loaded to prevent looping
    if (currentWorkflow && currentWorkflow.id === workflowId) {
      logger.info("✅ Workflow already loaded - skipping fetch", {
        workflowId: currentWorkflow.id,
        name: String(currentWorkflow.name),
      });
      dropOverlayForEditing();

      return;
    }

    // A load is already in flight. Wait for it, then check WHICH workflow it
    // produced: this guard used to return unconditionally once the other load
    // settled, so a request for wf-2 arriving while wf-1 was fetching resolved
    // successfully with the store holding wf-1 — the URL read wf-2 while the
    // canvas showed wf-1, and a Save then wrote to the wrong workflow. Only a
    // request for the id the other load delivered may piggyback on it; any
    // other id falls through to its own fetch. The loop re-checks because the
    // load this call defers to may itself have been superseded.
    if (isCurrentlyLoading) {
      logger.info("⏳ Workflow loading already in progress - waiting...");
      while (get().isLoadingWorkflow) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (get().workflow?.id === workflowId) {
        logger.info("✅ Workflow loading completed by another call");
        dropOverlayForEditing();

        return;
      }
      logger.info("↪️ Another call loaded a different workflow - fetching the requested one", {
        loadedWorkflowId: String(get().workflow?.id),
        requestedWorkflowId: workflowId,
      });
    }

    // Clear stale workflow immediately so UI doesn't flash old data
    setBaseAgentTools(null, null);
    set({ workflow: null, isLoadingWorkflow: true });

    try {
      logger.info("🌐 Fetching workflow from API", { workflowId });
      const workflow = await adminClient?.workflows.getWorkflow({
        workflowId: workflowId,
      });

      logger.info("📥 API response received", {
        hasWorkflow: String(!!workflow),
        workflowId: String(workflow?.id),
        workflowName: String(workflow?.name),
      });

      if (workflow) {
        logger.info("💾 Setting workflow in store", { workflowId: String(workflow.id) });
        // No snapshot is taken for an editor, which is what leaves the strip on
        // save and in the Code view inert: both treat a null snapshot as "there
        // is no overlay to undo".
        setBaseAgentTools(
          withToolPreferences ? captureBaseAgentTools(workflow as Workflow) : null,
          (workflow as Workflow).id,
        );
        const loaded = withToolPreferences
          ? applyToolPreferences(workflow as Workflow)
          : (workflow as Workflow);

        set({ workflow: loaded, isLoadingWorkflow: false });
        logger.info("✅ Workflow loaded successfully in store");
      } else {
        logger.error("❌ No workflow returned from API");
        set({ workflow: null, isLoadingWorkflow: false });
      }
    } catch (error) {
      logger.error("❌ Failed to load workflow", error);
      set({ workflow: null, isLoadingWorkflow: false });
      throw error;
    }
  },

  saveWorkflow: async (adminClient: WeBuildAiAdmin) => {
    logger.info("saveWorkflow", { workflowId: String(get().workflow?.id) });
    const currentWorkflow = get().workflow;

    if (!currentWorkflow) {
      return null;
    }

    // Strip per-user tool preferences before saving the shared workflow definition
    let workflowToSave = _baseAgentTools
      ? stripToolPreferences(currentWorkflow, _baseAgentTools)
      : currentWorkflow;

    // Validator of last resort for the id. A workflow can still reach save
    // with the blank seed id (e.g. one assembled purely on the canvas that
    // never passed through setWorkflowFromYaml), and an empty id turns the
    // upsert below into `PUT /workflows/` — the collection route — which the
    // admin API answers with an opaque 405 Method Not Allowed. Users never
    // author workflow ids, so generate one from the name rather than erroring,
    // and stamp it into the store so every later save reuses the same id.
    if (!isValidWorkflowId(workflowToSave.id)) {
      const generatedId = generateWorkflowId(workflowToSave.name);

      logger.info("Workflow reached save without a valid id; generated one", {
        previousId: String(workflowToSave.id),
        workflowId: generatedId,
      });
      workflowToSave = { ...workflowToSave, id: generatedId };
      set({ workflow: { ...currentWorkflow, id: generatedId } });
    }

    try {
      // The admin API upserts: PUT /workflows/{id} creates the workflow if it doesn't exist
      // (requires 'create' permission). We capture the response so the caller can use the
      // server-confirmed workflow (e.g. to navigate to its canonical id).
      const response = await adminClient?.workflows.updateWorkflow({
        workflowId: workflowToSave.id,
        workflowInput: workflowToSave,
      });

      if (!response) {
        // Some callers (e.g. the Workflow Builder's publish path) expect a
        // server-confirmed workflow to navigate to the canonical id; they
        // already handle a null return themselves. Callers that do not care
        // about the response (e.g. the playground's explicit toolbar save)
        // can safely ignore this.
        logger.info("Workflow save returned no response");

        return null;
      }

      // A snapshot exists only where the overlay is in play, so an editor's
      // save neither re-applies preferences over the definition it has just
      // published nor re-arms the snapshot that would make its next save lossy.
      const overlayActive = _baseAgentTools !== null;
      const savedWorkflow = overlayActive
        ? applyToolPreferences(response as Workflow)
        : (response as Workflow);

      setBaseAgentTools(
        overlayActive ? captureBaseAgentTools(response as Workflow) : null,
        (response as Workflow).id,
      );
      set({ workflow: savedWorkflow });
      logger.info("Workflow saved successfully", { workflowId: String(savedWorkflow.id) });

      return savedWorkflow;
    } catch (error) {
      logger.error("Failed to save workflow", error);
      throw error;
    }
  },

  setWorkflowName: (name: string) => {
    logger.info("setWorkflowName", { name });
    const currentWorkflow = get().workflow;

    if (currentWorkflow) {
      currentWorkflow.name = name;
      set({ workflow: { ...currentWorkflow } });
    }
  },

  setWorkflowDescription: (description: string) => {
    logger.info("setWorkflowDescription", { description });
    const currentWorkflow = get().workflow;

    if (currentWorkflow) {
      currentWorkflow.description = description;
      set({ workflow: { ...currentWorkflow } });
    }
  },

  setPersonality: (personality: ChatPersonalityType) => {
    logger.info("setPersonality", { personality });
    //TODO check if any agent.params in workflow has "personality", then set it
  },

  setWorkflowFromYaml: async (yamlString: string, platformClient: WebuildAiPlatform) => {
    logger.info("setWorkflowFromYaml");

    let parsedWorkflow:
      | (Workflow & { teams?: Record<string, YamlTeam>; agents: Record<string, YamlAgent> })
      | undefined;

    try {
      parsedWorkflow = yaml.load(yamlString) as Workflow & {
        teams?: Record<string, YamlTeam>;
        agents: Record<string, YamlAgent>;
      };
    } catch (yamlError: any) {
      throw new Error(
        yamlError.reason ||
          i18next.t("workflowEditor.syntaxError", {
            defaultValue: "The configuration contains a syntax error",
          }),
        { cause: yamlError },
      );
    }

    if (!parsedWorkflow || typeof parsedWorkflow !== "object") {
      throw new Error(
        i18next.t("workflowEditor.emptyConfig", {
          defaultValue: "The configuration appears to be empty or incomplete",
        }),
      );
    }

    if (!parsedWorkflow.agents || typeof parsedWorkflow.agents !== "object") {
      throw new Error(
        i18next.t("workflowEditor.missingAgents", {
          defaultValue: "The workflow configuration is missing its agents definition",
        }),
      );
    }

    const currentWorkflow = get().workflow;

    // Transform from snake_case (YAML) to camelCase (SDK)
    // This ensures fields like max_cycles -> maxCycles, user_interaction -> userInteraction
    const transformedWorkflow: Workflow = {
      ...parsedWorkflow,
      // The loaded workflow's id is immutable (PWI-158). The editor serialises the
      // id into the editable YAML, so a user could change or regenerate it; adopting
      // that value would turn a rename into a clone on save. Preserve the existing id
      // when a workflow is already loaded, and only fall back to the parsed id for a
      // brand-new workflow.
      //
      // `||` rather than `??` is deliberate: the Workflow Builder seeds a new
      // workflow with `id: ""` (createBlankWorkflow), which is blank but not
      // nullish, so `??` would lock the empty id in and discard whatever the
      // generated YAML carried. A blank current id must fall through to the
      // parsed id, and when the YAML has no usable id either — the Builder's
      // generator agent is not told the id convention — one is generated from
      // the name. Saving an empty id turns the admin upsert into
      // `PUT /workflows/`, which the API answers with an opaque 405.
      //
      // A system workflow id is never adopted from parsed YAML. The admin PUT
      // upserts, so a generated document that names itself `chat_agent` (a
      // format-valid id — the user asked the build agent for "a chat agent")
      // would have the next save replace the definition the tenant's Chat
      // page runs on, and `workflow_generator` would disable the Builder
      // itself. A workflow LOADED as a system id keeps it via
      // `currentWorkflow?.id` above, since that is its own immutable id.
      id:
        currentWorkflow?.id ||
        (isValidWorkflowId(parsedWorkflow.id) && !SYSTEM_WORKFLOW_IDS.has(parsedWorkflow.id)
          ? parsedWorkflow.id
          : generateWorkflowId(parsedWorkflow.name)),
      teams: transformTeamsFromYaml(parsedWorkflow.teams),
      agents: transformAgentsFromYaml(parsedWorkflow.agents),
    };

    // A saved cron schedule survives a regeneration (LSEG-107). The same reasoning
    // as the id above applies: the build agent regenerates the whole definition and
    // is not told the schedule convention, so a regenerated document that omits
    // `scheduled` would spread over an existing schedule and silently delete it.
    // That was harmless while nothing fired and now destroys a live schedule with no
    // error. A schedule is only ever removed deliberately, from /automations, which
    // sends an explicit null rather than an absent key.
    const parsedSchedule = (parsedWorkflow as { scheduled?: unknown }).scheduled;
    const currentSchedule = (currentWorkflow as { scheduled?: unknown } | undefined)?.scheduled;

    if (parsedSchedule === undefined && currentSchedule !== undefined) {
      (transformedWorkflow as { scheduled?: unknown }).scheduled = currentSchedule;
    }

    // The draft/published flag survives a round-trip for the same reason as the
    // id and the schedule above: no producer of YAML carries it. The build agent
    // is never told the convention, and the Code view is now shown the stripped
    // definition (`workflowToYamlObject`), so without this a code edit — or a
    // regenerated definition — silently republished a draft.
    const withStatus = preserveWorkflowStatus(transformedWorkflow, currentWorkflow);

    // Only update if the parsed workflow is different
    if (JSON.stringify(withStatus) === JSON.stringify(currentWorkflow)) {
      return;
    }
    //Test the workflow spec
    await platformClient.workflows.validate({
      workflow: withStatus,
    });
    set({ workflow: { ...withStatus } });
  },

  getWorkflow: () => get().workflow,
}));
